<template>
  <v-tooltip bottom :disabled="!showTooltip" color="rgba(0, 0, 0, 1)">
    <template #activator="{ on, attrs }">
      <span v-bind="attrs" :data-test="event.status" v-on="on">
        <span
          v-if="alwaysShow || event.status != 'registration_open'"
          :class="[
            texts[event.status].color,
            'chip',
            block ? 'chip-block' : ' px-2',
            border ? 'border' : '',
            large ? 'large-chip' : ''
          ]"
        >
          {{ texts[event.status].text }}
          <v-icon
            v-if="showLockIcon || showClockIcon"
            color="black"
            right
            style="font-size: 12px"
          >
            {{ showClockIcon ? "$iconClock" : "$iconLock" }}
          </v-icon>
          <v-icon
            v-if="event.unlisted"
            color="black"
            right
            style="font-size: 12px"
          >
            $iconEyeSlash
          </v-icon>
        </span>
        <template v-else-if="!noButton">
          <AppShowRegistrationBtn :event="event" class="d-print-none" />
          <div class="d-none d-print-block">
            {{ $t("common:button.register", "Register", "Register button") }}
            {{
              $t(
                "common:register.until.label",
                "Until:",
                "Register butten label Until:"
              )
            }}
            {{ $dayjs.date(event.registrationEndDate) }}
          </div>
        </template>
      </span>
    </template>
    <span>
      <template
        v-if="
          event.status == 'cancelled' &&
          event.cancelReasonId &&
          event.cancelReason
        "
      >
        {{ event.cancelReason.name }}
      </template>
      <template v-else-if="event.publicRegistrationOpensFrom">
        {{
          $t(
            [
              "events:eventStatus.publicRegistrationOpensFrom.tooltip",
              "Open for entries from: {\{publicRegistrationOpensFrom}\}"
            ],
            {
              publicRegistrationOpensFrom: $dayjs.date(
                event.publicRegistrationOpensFrom
              ),
              interpolation: { escapeValue: false }
            }
          )
        }}
      </template>
    </span>
  </v-tooltip>
</template>
<script>
import { defineComponent } from "vue";
import gql from "fraql";
import AppShowRegistrationBtn from "./AppShowRegistrationBtn.vue";

export default defineComponent({
  name: "AppShowEventStatus",
  components: {
    AppShowRegistrationBtn
  },
  props: {
    event: {
      type: Object,
      required: true
    },
    alwaysShow: {
      type: Boolean,
      default: false
    },
    noButton: {
      type: Boolean,
      default: false
    },
    border: {
      type: Boolean,
      default: false
    },
    block: {
      type: Boolean,
      default: false
    },
    large: {
      type: Boolean,
      default: false
    }
  },
  computed: {
    showLockIcon: ({ event }) =>
      ["CLOSING", "CLOSED"].includes(event.walletStatus),
    showClockIcon() {
      return (
        this.event.status == "registration_open" &&
        this.event.publicRegistrationOpensFrom &&
        this.$dayjs(this.event.publicRegistrationOpensFrom).utc() >
          this.$dayjs().utc()
      );
    },
    showTooltip() {
      if (this.event.cancelReasonId) return true;
      if (
        this.event.publicRegistrationOpensFrom &&
        this.event.status == "registration_open"
      )
        return true;
      return false;
    },
    texts() {
      return {
        draft: {
          text: this.$t(
            "events:event.status.Draft",
            "Draft",
            "Label for draft event status"
          ),
          color: "chip draft--text"
        },
        cancelled: {
          text: this.$t(
            "events:event.status.Cancelled",
            "Cancelled",
            "Label for cancelled event status"
          ),
          color: "chip cancelled--text"
        },
        proposed: {
          text: this.$t(
            "events:event.status.Proposed",
            "Proposed",
            "Label for proposed event status"
          ),
          color: "chip proposed--text"
        },
        declined: {
          text: this.$t(
            "events:event.status.Declined",
            "Declined",
            "Label for declined event status"
          ),
          color: "chip declined--text"
        },
        published: {
          text: this.$t(
            "events:event.status.Published",
            "Published",
            "Label for published event status"
          ),
          color: "chip published--text"
        },
        schedule_proposed: {
          text: this.$t(
            "events:event.status.ScheduleProposed",
            "Schedule proposed",
            "Label for schedule proposed event status"
          ),
          color: "chip schedule_proposed--text"
        },
        schedule_declined: {
          text: this.$t(
            "events:event.status.ScheduleDeclined",
            "Schedule declined",
            "Label for schedule declined event status"
          ),
          color: "chip schedule_declined--text"
        },
        registration_open: {
          text: this.$t(
            "events:event.status.RegistrationOpen",
            "Registration open",
            "Label for registration open event status"
          ),
          color: "chip registration_open--text"
        },
        registration_closed: {
          text: this.$t(
            "events:event.status.RegistrationClosed",
            "Registration closed",
            "Label for registration closed event status"
          ),
          color: "chip registration_closed--text"
        },
        event_running: {
          text: this.$t(
            "events:event.status.EventRunning",
            "LIVE",
            "Label for event running event status"
          ),
          color: "chip event_running--text"
        },
        event_ended: {
          text: this.$t(
            "events:event.status.EventEnded",
            "Event ended",
            "Label for ended event status"
          ),
          color: "chip event_ended--text"
        },
        results_published: {
          text: this.$t(
            "events:event.status.ResultsPublished",
            "Results published",
            "Label for results published event status"
          ),
          color: "chip results_published--text"
        },
        closed: {
          text: this.$t(
            "events:event.status.Closed",
            "Closed",
            "Label for closed event status"
          ),
          color: "chip closed--text"
        },
        discarded: {
          text: this.$t(
            "events:event.status.Discarded",
            "Discarded",
            "Label for closed event status"
          ),
          color: "chip discarded--text"
        },
        unknown: {
          text: this.$t(
            "events:event.status.Unknown",
            "Unknown",
            "Label for unknown event status"
          ),
          color: "chip unknown--text"
        }
      };
    }
  },
  fragments: {
    eventStatus: gql`
      fragment _ on Event {
        status
        publicRegistrationOpensFrom
        unlisted
        ${AppShowRegistrationBtn.fragments.event}
        cancelReasonId
        cancelReason @client {
          id
          name
        }
        walletStatus
      }
    `
  }
});
</script>
<style scoped>
.chip {
  background-color: white;
}
.border {
  border: 1px solid black;
}
.large-chip {
  height: 20px !important;
}
.chip-block {
  padding: 2px 10px;
  border: none;
}
</style>
